import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult
} from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import type {
  FilePickerCommandMessage,
  FilePickerConfig,
  FilePickerExtensionFilter,
  FilePickerInitializeMessage,
  FilePickerItem,
  FilePickerMessage,
  FilePickerNotificationMessage
} from "../types/onedrive";
import { showSubframeToast } from "../components/common/CasePrepToast";
import { PickedItem, flattenSharePointTree } from "./pickerTraversal";

const MICROSOFT_CLIENT_ID = import.meta.env.REACT_APP_MICROSOFT_CLIENT_ID;
const MICROSOFT_AUTHORITY = "https://login.microsoftonline.com/common";
const GRAPH_RESOURCE = "https://graph.microsoft.com";
const GRAPH_DRIVE_ENDPOINT = `${GRAPH_RESOURCE}/v1.0/me/drive`;
const POPUP_FEATURES = "width=1080,height=680,menubar=no,toolbar=no";
const PICKER_TIMEOUT_MS = 5 * 60 * 1000;
const SHAREPOINT_HOST_SUFFIXES = [
  ".sharepoint.com",
  ".sharepoint.us",
  ".sharepoint.de",
  ".sharepoint.cn",
  ".sharepoint-mil.us"
];
const SUPPORTED_FILE_EXTENSIONS: FilePickerExtensionFilter[] = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".doc",
  ".docx",
  ".xlsx",
  ".xls",
  ".ppt",
  ".pptx"
];

if (!MICROSOFT_CLIENT_ID) {
  throw new Error("MICROSOFT_CLIENT_ID environment variable is not set");
}

const APP_ORIGIN = window.location.origin;
const MSAL_REDIRECT_URI = `${APP_ORIGIN}/msal-redirect.html`;

let msalApp: PublicClientApplication | null = null;
let msalInitializePromise: Promise<void> | null = null;

class UnsupportedTokenResourceError extends Error {
  constructor(resource: string) {
    super(`Unsupported Microsoft token resource requested: ${resource}`);
    this.name = "UnsupportedTokenResourceError";
  }
}

const getMsalApp = async (): Promise<PublicClientApplication> => {
  if (!msalApp) {
    msalApp = new PublicClientApplication({
      auth: {
        clientId: MICROSOFT_CLIENT_ID,
        authority: MICROSOFT_AUTHORITY,
        redirectUri: MSAL_REDIRECT_URI
      },
      cache: {
        cacheLocation: "localStorage"
      }
    });
  }

  if (!msalInitializePromise) {
    msalInitializePromise = msalApp.initialize();
  }

  await msalInitializePromise;
  return msalApp;
};

getMsalApp().catch(() => undefined);

const getActiveAccount = (app: PublicClientApplication): AccountInfo | null => {
  const activeAccount = app.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const [firstAccount] = app.getAllAccounts();
  if (firstAccount) {
    app.setActiveAccount(firstAccount);
    return firstAccount;
  }

  return null;
};

const normalizeResource = (resource: string): string => {
  try {
    const { origin, hostname } = new URL(resource);
    const normalizedHostname = hostname.toLowerCase();

    if (origin === GRAPH_RESOURCE) {
      return origin;
    }

    if (
      SHAREPOINT_HOST_SUFFIXES.some((suffix) =>
        normalizedHostname.endsWith(suffix)
      )
    ) {
      return origin;
    }

    throw new UnsupportedTokenResourceError(resource);
  } catch {
    const normalizedResource = resource.replace(/\/$/, "");
    if (normalizedResource === GRAPH_RESOURCE) {
      return normalizedResource;
    }

    throw new UnsupportedTokenResourceError(resource);
  }
};

const getTokenForResource = async (
  resource: string,
  claims?: string
): Promise<string> => {
  const app = await getMsalApp();
  const scopes = [`${normalizeResource(resource)}/.default`];
  const account = getActiveAccount(app);
  const popupRequest = {
    scopes,
    claims,
    redirectUri: MSAL_REDIRECT_URI,
    responseMode: "fragment" as const
  };

  if (!account || claims) {
    const popupResult: AuthenticationResult = await app.acquireTokenPopup(
      popupRequest
    );
    app.setActiveAccount(popupResult.account);
    return popupResult.accessToken;
  }

  try {
    const silentResult = await app.acquireTokenSilent({
      scopes,
      account,
      claims
    });
    return silentResult.accessToken;
  } catch (error) {
    const popupResult: AuthenticationResult = await app.acquireTokenPopup(
      popupRequest
    );
    app.setActiveAccount(popupResult.account);
    return popupResult.accessToken;
  }
};

const getFileExtension = (fileName: string): string | undefined => {
  return fileName.split(".").pop()?.toLowerCase();
};

const getMimeType = (fileName: string, mimeType?: string): string => {
  if (mimeType) {
    return mimeType;
  }

  const extension = getFileExtension(fileName);
  switch (extension) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "doc":
      return "application/msword";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "xls":
      return "application/vnd.ms-excel";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "ppt":
      return "application/vnd.ms-powerpoint";
    default:
      return "application/octet-stream";
  }
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    if (error.message.includes("popup_window_error")) {
      return "Microsoft sign-in popup was blocked. Please allow popups for CaseBlink and try again.";
    }

    return error.message;
  }

  if (typeof error === "string") {
    if (error.includes("popup_window_error")) {
      return "Microsoft sign-in popup was blocked. Please allow popups for CaseBlink and try again.";
    }

    return error;
  }

  return "Unknown error occurred";
};

const createChannelId = (): string => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const joinUrl = (baseUrl: string, path: string): string => {
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

const getPickerBaseUrl = async (): Promise<string> => {
  const graphToken = await getTokenForResource(GRAPH_RESOURCE);
  const response = await fetch(GRAPH_DRIVE_ENDPOINT, {
    headers: {
      Authorization: `Bearer ${graphToken}`
    }
  });

  if (!response.ok) {
    throw new Error(
      `Unable to load Microsoft drive information (${response.status})`
    );
  }

  const drive = (await response.json()) as { webUrl?: string };
  if (!drive.webUrl) {
    throw new Error("Microsoft did not return a OneDrive URL for this user.");
  }

  return new URL(drive.webUrl).origin;
};

const preparePickerTokens = async (): Promise<string> => {
  const baseUrl = await getPickerBaseUrl();
  await getTokenForResource(baseUrl);
  return baseUrl;
};

const createPickerConfig = (channelId: string): FilePickerConfig => ({
  sdk: "8.0",
  entry: {
    oneDrive: {
      files: {
        fallbackToRoot: true
      }
    }
  },
  authentication: {
    enabled: true,
    tokens: {
      graph: true,
      sharePoint: true
    },
    claimsChallenge: {
      enabled: true
    }
  },
  messaging: {
    origin: APP_ORIGIN,
    channelId
  },
  search: {
    enabled: false
  },
  typesAndSources: {
    mode: "all",
    filters: [...SUPPORTED_FILE_EXTENSIONS, "folder"],
    access: {
      mode: "read"
    },
    pivots: {
      oneDrive: true,
      recent: true,
      shared: true,
      sharedLibraries: true,
      myOrganization: false,
      site: false
    }
  },
  selection: {
    mode: "multiple",
    enablePersistence: true
  },
  commands: {
    pick: {
      action: "select",
      select: {
        urls: {
          download: true
        }
      },
      label: "Upload"
    },
    upload: {
      enabled: false
    },
    createFolder: {
      enabled: false
    }
  },
  title: "Choose files from SharePoint"
});

const openPickerWindow = (): Window => {
  const pickerWindow = window.open("", "SharePointPicker", POPUP_FEATURES);

  if (!pickerWindow) {
    throw new Error(
      "The Microsoft picker popup was blocked. Please allow popups for CaseBlink."
    );
  }

  pickerWindow.document.write(
    "<!doctype html><title>Loading Microsoft files...</title><body>Loading Microsoft files...</body>"
  );

  return pickerWindow;
};

const submitPickerForm = async (
  pickerWindow: Window,
  baseUrl: string,
  config: FilePickerConfig
): Promise<void> => {
  const accessToken = await getTokenForResource(baseUrl);
  const queryString = new URLSearchParams({
    filePicker: JSON.stringify(config),
    locale: navigator.language?.toLowerCase() || "en-us"
  });
  const form = pickerWindow.document.createElement("form");
  form.setAttribute(
    "action",
    `${joinUrl(baseUrl, "_layouts/15/FilePicker.aspx")}?${queryString}`
  );
  form.setAttribute("method", "POST");

  const tokenInput = pickerWindow.document.createElement("input");
  tokenInput.setAttribute("type", "hidden");
  tokenInput.setAttribute("name", "access_token");
  tokenInput.setAttribute("value", accessToken);
  form.appendChild(tokenInput);

  pickerWindow.document.body.append(form);
  form.submit();
};

const getItemEndpoint = (item: FilePickerItem): string => {
  if (!item["@sharePoint.endpoint"] || !item.parentReference?.driveId) {
    throw new Error(`Microsoft did not return enough data for ${item.name}.`);
  }

  return joinUrl(
    item["@sharePoint.endpoint"],
    `drives/${item.parentReference.driveId}/items/${item.id}`
  );
};

const downloadPickedItem = async (item: FilePickerItem): Promise<File> => {
  const itemEndpoint = getItemEndpoint(item);
  const sharePointToken = await getTokenForResource(itemEndpoint);
  const metadataResponse = await fetch(itemEndpoint, {
    headers: {
      Authorization: `Bearer ${sharePointToken}`
    }
  });

  if (!metadataResponse.ok) {
    throw new Error(`Unable to read metadata for ${item.name}.`);
  }

  const metadata = (await metadataResponse.json()) as FilePickerItem;
  const fileName = metadata.name || item.name || "SharePoint file";
  const directDownloadUrl =
    item["@microsoft.graph.downloadUrl"] ||
    item["@content.downloadUrl"] ||
    metadata["@microsoft.graph.downloadUrl"] ||
    metadata["@content.downloadUrl"];
  const downloadResponse = await fetch(
    directDownloadUrl || joinUrl(itemEndpoint, "content"),
    directDownloadUrl
      ? undefined
      : {
          headers: {
            Authorization: `Bearer ${sharePointToken}`
          }
        }
  );

  if (!downloadResponse.ok) {
    throw new Error(`Unable to download ${fileName}.`);
  }

  const blob = await downloadResponse.blob();
  return new File([blob], fileName, {
    type: getMimeType(fileName, metadata.file?.mimeType || item.file?.mimeType)
  });
};

/**
 * Lists a picked folder's children via Graph, following `@odata.nextLink`.
 *
 * Graph responses don't carry the picker's `@sharePoint.endpoint` annotation,
 * so we re-attach the parent's endpoint to each child so downstream downloads
 * and deeper listings can build item URLs the same way.
 */
const listSharePointChildren = async (
  item: FilePickerItem
): Promise<FilePickerItem[]> => {
  const endpoint = getItemEndpoint(item);
  const token = await getTokenForResource(endpoint);
  const base = item["@sharePoint.endpoint"] as string;

  const collect = async (url: string): Promise<FilePickerItem[]> => {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error(`Unable to list contents of ${item.name || "folder"}.`);
    }

    const data = (await response.json()) as {
      value?: FilePickerItem[];
      "@odata.nextLink"?: string;
    };
    const children = (data.value ?? []).map((child) => ({
      ...child,
      "@sharePoint.endpoint": base
    }));
    const next = data["@odata.nextLink"];
    if (next) {
      const rest = await collect(next);
      return [...children, ...rest];
    }
    return children;
  };

  return collect(`${endpoint}/children`);
};

const isInitializeMessage = (
  message: FilePickerMessage,
  channelId: string
): message is FilePickerInitializeMessage => {
  return message.type === "initialize" && message.channelId === channelId;
};

const isNotificationMessage = (
  message: FilePickerMessage
): message is FilePickerNotificationMessage => {
  return message.type === "notification";
};

const isCommandMessage = (
  message: FilePickerMessage
): message is FilePickerCommandMessage => {
  return message.type === "command";
};

const openFilePicker = async (
  pickerWindow: Window,
  baseUrl: string
): Promise<PickedItem[]> => {
  const channelId = createChannelId();
  const config = createPickerConfig(channelId);

  return new Promise<PickedItem[]>((resolve, reject) => {
    let port: MessagePort | null = null;
    let settled = false;
    let closeInterval: number | undefined;
    let timeout: number | undefined;

    const cleanup = () => {
      settled = true;
      window.removeEventListener("message", initializeMessageListener);
      if (port) {
        port.removeEventListener("message", channelMessageListener);
      }
      if (closeInterval !== undefined) {
        window.clearInterval(closeInterval);
      }
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
    };

    const finish = (items: PickedItem[]) => {
      cleanup();
      pickerWindow.close();
      resolve(items);
    };

    const fail = (error: unknown) => {
      cleanup();
      pickerWindow.close();
      reject(error);
    };

    closeInterval = window.setInterval(() => {
      if (!settled && pickerWindow.closed) {
        cleanup();
        resolve([]);
      }
    }, 500);

    timeout = window.setTimeout(() => {
      if (!settled) {
        fail(new Error("Timed out waiting for Microsoft file selection."));
      }
    }, PICKER_TIMEOUT_MS);

    async function channelMessageListener(
      event: MessageEvent<FilePickerMessage>
    ): Promise<void> {
      const payload = event.data;

      if (isNotificationMessage(payload)) {
        return;
      }

      if (!isCommandMessage(payload)) {
        return;
      }

      port?.postMessage({
        type: "acknowledge",
        id: payload.id
      });

      const command = payload.data;

      try {
        switch (command.command) {
          case "authenticate": {
            if (typeof command.resource !== "string") {
              throw new Error(
                "Microsoft picker did not request a valid resource."
              );
            }

            try {
              const token = await getTokenForResource(
                command.resource,
                typeof command.claims === "string" ? command.claims : undefined
              );
              port?.postMessage({
                type: "result",
                id: payload.id,
                data: {
                  result: "token",
                  token
                }
              });
            } catch (error) {
              if (error instanceof UnsupportedTokenResourceError) {
                Sentry.captureMessage(
                  "SharePoint picker requested unsupported token resource",
                  {
                    level: "warning",
                    tags: {
                      feature: "sharepoint-picker",
                      command: command.command
                    },
                    extra: {
                      resource: command.resource
                    }
                  }
                );
                port?.postMessage({
                  type: "result",
                  id: payload.id,
                  data: {
                    result: "error",
                    error: {
                      code: "unsupportedResource",
                      message:
                        "CaseBlink only supports Microsoft Graph and SharePoint token resources."
                    }
                  }
                });
                break;
              }

              throw error;
            }
            break;
          }
          case "pick": {
            const items = Array.isArray(command.items) ? command.items : [];
            showSubframeToast({
              title: `Retrieving ${items.length} item${
                items.length === 1 ? "" : "s"
              } from SharePoint...`,
              variant: "neutral",
              icon: "FeatherLoader",
              durationMs: 1500
            });

            const picked = await flattenSharePointTree(
              items,
              listSharePointChildren,
              (item) => downloadPickedItem(item)
            );
            port?.postMessage({
              type: "result",
              id: payload.id,
              data: {
                result: "success"
              }
            });

            showSubframeToast({
              title: `Successfully retrieved ${picked.length} file${
                picked.length === 1 ? "" : "s"
              } from SharePoint`,
              variant: "success",
              icon: "FeatherCheck",
              durationMs: 3000
            });
            finish(picked);
            break;
          }
          case "close":
            port?.postMessage({
              type: "result",
              id: payload.id,
              data: {
                result: "success"
              }
            });
            finish([]);
            break;
          default:
            port?.postMessage({
              type: "result",
              id: payload.id,
              data: {
                result: "error",
                error: {
                  code: "unsupportedCommand",
                  message: String(command.command)
                }
              }
            });
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: "sharepoint-picker",
            command: command.command
          },
          extra: {
            resource:
              "resource" in command && typeof command.resource === "string"
                ? command.resource
                : undefined,
            itemCount:
              "items" in command && Array.isArray(command.items)
                ? command.items.length
                : undefined
          }
        });

        port?.postMessage({
          type: "result",
          id: payload.id,
          data: {
            result: "error",
            error: {
              code: "commandFailed",
              message: getErrorMessage(error)
            }
          }
        });
        fail(error);
      }
    }

    function initializeMessageListener(event: MessageEvent<FilePickerMessage>) {
      if (event.source !== pickerWindow) {
        return;
      }

      const message = event.data;
      if (!isInitializeMessage(message, channelId)) {
        return;
      }

      [port] = event.ports;
      if (!port) {
        fail(new Error("Microsoft picker did not provide a messaging port."));
        return;
      }

      port.addEventListener("message", channelMessageListener);
      port.start();
      port.postMessage({
        type: "activate"
      });
    }

    window.addEventListener("message", initializeMessageListener);

    submitPickerForm(pickerWindow, baseUrl, config).catch(fail);
  });
};

export const useSharePoint = () => {
  // Files and folders, keeping each file's relative path.
  const pickItems = async (): Promise<PickedItem[]> => {
    let pickerWindow: Window | null = null;

    try {
      const baseUrl = await preparePickerTokens();
      pickerWindow = openPickerWindow();
      return await openFilePicker(pickerWindow, baseUrl);
    } catch (error) {
      pickerWindow?.close();
      Sentry.captureException(error, {
        tags: {
          feature: "sharepoint-picker"
        },
        extra: {
          origin: APP_ORIGIN,
          redirectUri: MSAL_REDIRECT_URI
        }
      });
      showSubframeToast({
        title: "Error connecting to Microsoft account",
        description: getErrorMessage(error) || "Please try again",
        variant: "error",
        icon: "FeatherAlertCircle",
        durationMs: 5000
      });
      return [];
    }
  };

  // Files only, flattened, for callers that don't preserve folder structure.
  const pickFiles = async (): Promise<File[]> =>
    (await pickItems()).map((item) => item.file);

  return { pickFiles, pickItems };
};
